import '../../styles/restScreens.css';
import React, { useState, useMemo } from 'react';
import { getAllWidgets, WIDGET_CATEGORIES, createWidgetInstance, isWidgetAllowed } from './widgetRegistry';

/**
 * WidgetLibrary Component
 *
 * Modal/sidebar component for browsing and adding widgets to the dashboard
 */
const WidgetLibrary = ({ isOpen, onClose, onAddWidget, areaAccess = {} }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Get all widgets
  const allWidgets = useMemo(
    () => getAllWidgets().filter((widget) => isWidgetAllowed({ type: widget.id, config: widget.defaultConfig }, areaAccess)),
    [areaAccess]
  );

  // Filter widgets based on category and search
  const filteredWidgets = useMemo(() => {
    let widgets = allWidgets;

    // Filter by category
    if (selectedCategory !== 'all') {
      widgets = widgets.filter(widget => widget.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      widgets = widgets.filter(widget =>
        widget.name.toLowerCase().includes(term) ||
        widget.description.toLowerCase().includes(term)
      );
    }

    return widgets;
  }, [allWidgets, selectedCategory, searchTerm]);

  const handleAddWidget = (widgetType) => {
    const widgetInstance = createWidgetInstance(widgetType);
    if (onAddWidget) {
      onAddWidget(widgetInstance);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div data-m6-layout="widgets" data-m6-component="WidgetLibrary" className="fixed right-0 top-0 h-full w-96 bg-[var(--surface-raised)] shadow-2xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--surface-border)]">
          <div>
            <h2 className="text-xl font-bold text-[var(--ink)]">
              Widget Library
            </h2>
            <p className="text-sm text-[var(--ink-muted)] mt-1">
              Choose widgets to add to your dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-[var(--surface-border)]">
          <div className="relative">
            <input
              type="text"
              placeholder="Search widgets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[var(--surface-border)] rounded-lg
                       bg-[var(--surface-sunken)] text-[var(--ink)]
                       focus:ring-2 focus:ring-[var(--accent-ink)]/20 focus:border-[var(--accent-ink)]"
            />
            <svg className="w-5 h-5 absolute left-3 top-2.5 text-[var(--ink-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex overflow-x-auto border-b border-[var(--surface-border)]">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              selectedCategory === 'all'
              ? 'border-[var(--nav-active-edge)] text-[var(--nav-active-ink)]'
              : 'border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]'
            }`}
          >
            All Widgets
          </button>
          {Object.values(WIDGET_CATEGORIES).map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                selectedCategory === category
                ? 'border-[var(--nav-active-edge)] text-[var(--nav-active-ink)]'
                : 'border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Widget List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {filteredWidgets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg className="w-16 h-16 text-[var(--ink-muted)] mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-[var(--ink-muted)]">
                No widgets found
              </p>
            </div>
          ) : (
            filteredWidgets.map(widget => (
              <div
                key={widget.id}
                className="border border-[var(--surface-border)] rounded-lg p-4 bg-[var(--surface-raised)]
                         hover:border-[var(--nav-active-edge)] hover:shadow-md
                         transition-all duration-200 cursor-pointer group"
                onClick={() => handleAddWidget(widget.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{widget.icon}</span>
                    <h3 className="text-sm font-semibold text-[var(--ink)]">
                      {widget.name}
                    </h3>
                  </div>
                  <button
                    className="p-1.5 rounded bg-[var(--btn-fill)] text-[var(--btn-ink)] opacity-0 group-hover:opacity-100
                             transition-opacity duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddWidget(widget.id);
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>

                <p className="text-xs text-[var(--ink-muted)] mb-2">
                  {widget.description}
                </p>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-[var(--ink-muted)]">
                    {widget.category}
                  </span>
                  <span className="text-xs text-[var(--ink-muted)]">
                    {widget.defaultSize.w}x{widget.defaultSize.h}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--surface-border)] bg-[var(--surface-sunken)]">
          <p className="text-xs text-[var(--ink-muted)] text-center">
            Click on any widget to add it to your dashboard
          </p>
        </div>
      </div>
    </>
  );
};

export default WidgetLibrary;
